import { useCallback } from "react";
import useApi from "./useApi";
import { postIfIdentityLookup } from "../services/pifService";
import { mapIdentityLookupResult } from "../pages/supervision/PostInterviewForm/pifLookup";
import { PostIfIdentityLookupRequestDto, PostIfIdentityLookupResultDto } from "../types/pifDto";
import { PifIdentityLookupOutcome, PifIdentityMatch } from "../features/workflows/workflowRunData";

type LookupResponse =
  | { kind: "found"; dto: PostIfIdentityLookupResultDto }
  | { kind: "forbidden" }
  | { kind: "rate-limited" };

/** Read structurally rather than through axios.isAxiosError — see pifWriteResult.ts. */
const httpStatusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } } | null)?.response?.status;

/** 403 and 429 are outcomes the stage explains in place, so they resolve as data instead of raising
 *  useApi's alert. Anything else rejects, so useApi alerts and the stage says it couldn't reach the
 *  directory. */
const lookUp = async (request: PostIfIdentityLookupRequestDto): Promise<LookupResponse> => {
  try {
    return { kind: "found", dto: await postIfIdentityLookup(request) };
  } catch (error) {
    const status = httpStatusOf(error);
    if (status === 403) return { kind: "forbidden" };
    if (status === 429) return { kind: "rate-limited" };
    throw error;
  }
};

const toMatch = (dto: PostIfIdentityLookupResultDto): PifIdentityMatch => {
  const result = mapIdentityLookupResult(dto);
  if (result.kind === "blocked") {
    return {
      flow: "blocked",
      name: "",
      employeeId: result.employeeId ? String(result.employeeId) : "",
      dateOfBirth: "",
    };
  }
  if (result.kind === "new-applicant") {
    return { flow: "new", name: "", employeeId: "", dateOfBirth: "" };
  }

  const { candidate } = result;
  return {
    flow: result.flow === "active" ? "existing-active" : "rehire",
    name: [candidate.first, candidate.middle, candidate.last].filter(Boolean).join(" "),
    employeeId: candidate.employeeId ? String(candidate.employeeId) : "",
    dateOfBirth: candidate.dob,
    // Contact details only come back for a rehire; an active employee maintains their own.
    phone: candidate.cell || candidate.home || undefined,
    email: candidate.email || undefined,
    firstName: candidate.first,
    middleName: candidate.middle,
    lastName: candidate.last,
    address1: candidate.addr1,
    city: candidate.city,
    state: candidate.state,
    zip: candidate.zip,
    homePhone: candidate.home,
    cell: candidate.cell,
  };
};

/**
 * The PIF Start stage's identity lookup against the real Enterprise records — the same
 * POST /postif/identity-lookup the PostIF start screen uses, reduced to the four flows the
 * workflow screens render.
 *
 * Only the digits are sent, never the formatted value, and the typed SSN never leaves this call:
 * it is POSTed (never in a URL), and nothing returned here carries it — the API sends the SSN
 * fully redacted and mapIdentityLookupResult drops even that.
 */
const useWorkflowPifIdentityLookup = () => {
  const { executeApi } = useApi();

  return useCallback(
    async (lookupValue: string): Promise<PifIdentityLookupOutcome> => {
      const digits = lookupValue.replace(/\D/g, "");
      const response = await executeApi<PostIfIdentityLookupRequestDto, LookupResponse>(lookUp, {
        lookupValue: digits,
      });
      // Null means useApi already raised the alert.
      if (!response) return { kind: "failed" };
      if (response.kind !== "found") return response;
      return { kind: "matched", match: toMatch(response.dto) };
    },
    [executeApi],
  );
};

export default useWorkflowPifIdentityLookup;
